//Built-in libraries

import Card from '@/components/UI/Card';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
//Services
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
//Assets and Miscellaneous
import type { DashboardType } from '@/types/Analytics.ts';
import { CurrencyFormatDisplay } from '../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
import Table from '../../UI/Table';
import { Widget } from '../constants';
//Custom components
import WidgetLayout from '../WidgetLayout';

interface NeighborhoodImpactWidgetProps {
  dashboard: DashboardType;
}

const NeighborhoodImpactWidget = ({ dashboard }: NeighborhoodImpactWidgetProps) => {
  const [perPage, setPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);

  const fetchNeighborhoodImpactWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_BY_NEIGHBORHOOD,
  });

  const neighborhoodImpactWidgetData = fetchNeighborhoodImpactWidgetData?.data?.data;

  const totalPage = Math.max(1, Math.ceil((neighborhoodImpactWidgetData?.length ?? 0) / perPage));

  const pagedData = useMemo(() => {
    if (!neighborhoodImpactWidgetData) {
      return [];
    }
    const start = (page - 1) * perPage;
    return neighborhoodImpactWidgetData.slice(start, start + perPage);
  }, [neighborhoodImpactWidgetData, page, perPage]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPage));
  }, [totalPage]);

  const nextPageHandler = () => {
    setPage((prev) => Math.min(prev + 1, totalPage));
  };

  const previousPageHandler = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'neighborhood',
      header: 'NEIGHBORHOOD',
      accessorKey: 'neighborhood',
      cell: (props) => {
        const { getValue } = props;
        const value = getValue() as string;
        if (value.trim() === '') {
          return 'Unknown';
        }
        return value;
      },
    },
    {
      id: 'projectCount',
      header: () => <div className='text-end'>PROJECTS</div>,
      accessorKey: '_count',
      cell: (props) => {
        const { getValue } = props;
        const value = getValue() as number;
        return (
          <div className='text-end'>
            <span>{value}</span>
          </div>
        );
      },
    },
    {
      id: 'totalDevelopmentValue',
      header: () => <div className='text-end'>TOTAL DEVELOPMENT VALUE</div>,
      accessorKey: 'totalDevelopmentCost',
      cell: (props) => {
        const { getValue } = props;
        const value = getValue() as number;
        return (
          <div className='text-end'>
            <CurrencyFormatDisplay value={value} prefix='$' />
          </div>
        );
      },
    },
  ];

  const tablePagination = {
    page: page - 1,
    pageSize: perPage,
    pageCount: totalPage,
    currentPage: page,
    setPageSize: (v: number) => {
      setPerPage(v);
      setPage(1);
    },
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  return (
    <WidgetLayout dashboard={dashboard} widget={Widget.PROJECTS_BY_NEIGHBORHOOD}>
      {neighborhoodImpactWidgetData && (
        <Card title='Neighborhood Impact' error={false}>
          <div className='w-full overflow-x-auto'>
            <Table cols={tableColumns} tableData={pagedData} pagination={tablePagination} />
          </div>
        </Card>
      )}
    </WidgetLayout>
  );
};

export default NeighborhoodImpactWidget;
